import { canonicalIngredientType, componentKitchenName } from './plating.js'

export const CONTAINER_LABEL_GROUPS = [
  { id: 'sauce', title: 'Sauces', stage: 'SAUCE', copies: 2 },
  { id: 'carb', title: 'Carbs', stage: 'CARB', copies: 4 },
  { id: 'vegetable', title: 'Vegetables', stage: 'VEGETABLE', copies: 2 },
  { id: 'protein_raw', title: 'Raw proteins', stage: 'RAW PROTEIN', copies: 4 },
  { id: 'protein_cooked', title: 'Cooked proteins', stage: 'COOKED PROTEIN', copies: 4 },
]

export function evenCopies(value, fallback = 2) {
  const n = Number(value)
  return Number.isFinite(n) ? Math.max(2, Math.min(50, Math.ceil(n / 2) * 2)) : fallback
}

export function buildContainerLabelGroups(menu, copies = {}) {
  const groups = CONTAINER_LABEL_GROUPS.map((g) => ({ ...g, copies: evenCopies(copies[g.id] ?? g.copies), items: [] }))
  const seen = new Set()
  function add(groupId, component) {
    const name = componentKitchenName(component)
    if (!name) return
    // Menu swaps on older servers retain the original recipe id. Include the
    // visible preparation name so a swapped vegetable cannot lose its label.
    const identity = `name:${name.toLowerCase().replace(/\s+/g, ' ')}`
    const key = `${groupId}:${identity}`
    if (seen.has(key)) return
    seen.add(key)
    groups.find((g) => g.id === groupId).items.push({ key, name })
  }
  for (const dish of menu?.dishes || []) {
    for (const component of dish.components || []) {
      const type = canonicalIngredientType(component.kitchen_type || component.component_type, component.name)
      if (type === 'protein') {
        add('protein_raw', component)
        add('protein_cooked', component)
      } else if (['sauce', 'carb', 'vegetable'].includes(type)) {
        add(type, component)
      }
    }
  }
  for (const recipe of menu?.recipe_dependencies || []) {
    if (recipe.category === 'sauce') add('sauce', recipe)
  }
  return groups
}

const escapeHtml = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))

export function containerLabelsDocument(groups, title, sheet = false) {
  const cells = groups.flatMap((group) => group.items.flatMap((item) =>
    Array.from({ length: evenCopies(group.copies) }, () => `<section class="label"><div class="stage">${escapeHtml(group.stage)}</div><strong data-fit-label>${escapeHtml(item.name)}</strong><div class="date">Date: ______________</div></section>`)))
  if (!cells.length) throw new Error('No kitchen container labels for this menu')
  const pages = []
  for (let i = 0; i < cells.length; i += 40) pages.push(`<div class="sheet">${cells.slice(i, i + 40).join('')}</div>`)
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>
* { box-sizing: border-box; } html, body { margin: 0; padding: 0; color: #000; background: #fff; font-family: Arial, sans-serif; }
@page { size: ${sheet ? 'letter' : '2in 1in'}; margin: 0; }
.label { width: 2in; height: 1in; padding: .055in .08in; display: flex; flex-direction: column; justify-content: space-between; text-align: center; break-inside: avoid; ${sheet ? 'border: .5pt solid #aaa;' : 'break-after: page;'} }
.label:last-child { break-after: auto; }
.stage { font-size: 8pt; font-weight: bold; letter-spacing: .4pt; }
strong { font-size: 10pt; line-height: 1.05; overflow-wrap: anywhere; display: block; height: .55in; overflow: hidden; }
.date { font-size: 7pt; }
.sheet { width: 8.5in; height: 11in; padding: .5in .25in; display: grid; grid-template-columns: repeat(4, 2in); grid-auto-rows: 1in; align-content: start; break-after: page; }
.sheet:last-child { break-after: auto; }
</style></head><body>${sheet ? pages.join('') : cells.join('')}</body></html>`
}
